// g2-plan-size-high-water-mark.ts
//
// Bug L, second path (P16, D16). G2's ticket-count condition was computed
// from the LIVE plan, so a worker could cancel one ordinary ticket out of 16
// and watch the gate fall back under the threshold. Measured:
//     { cancelOk: true, beforeRequired: true, afterRequired: false, afterWaivable: true }
//
// The deploy-ticket half of Bug L was closed by refusing the proposal
// outright (proposal-applier.ts), but that cannot work here: any of the 16
// tickets will do, so a blanket "no cancelling" rule would break legitimate
// plan pruning. Instead the SIGNAL is made durable — once a plan has been
// seen above the threshold, that observation persists and G2 stays required.
// Shrinking a plan that was once large is itself worth a human glance, not a
// reason to skip the gate.
//
// Safety property worth stating: this value can only ever ADD a reason to
// require G2, never remove one. That is why every failure path here returns
// 0 (an inert value that cannot exceed a positive threshold) instead of
// throwing — a damaged file degrades to pre-fix behaviour, never to
// something laxer than it.
//
// Stored in the mission control root, which sits outside the worker's git
// worktree — the same isolation the actor binding relies on (Bug J).
import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { writeFileDurable } from "./durable-file-writer.js";

const MARK_FILE_NAME = "g2-plan-size-high-water-mark.json";

function markPath(controlRoot: string): string {
  return join(controlRoot, MARK_FILE_NAME);
}

/**
 * Largest effective plan size ever observed for this mission. Returns 0 when
 * absent or unreadable — see the safety note above: 0 cannot exceed a
 * positive threshold, so an unreadable mark never grants a waiver.
 */
export async function readG2PlanSizeHighWaterMark(controlRoot: string): Promise<number> {
  let raw: string;
  try {
    raw = await readFile(markPath(controlRoot), "utf8");
  } catch {
    return 0;
  }
  try {
    const parsed = JSON.parse(raw) as { plan_size?: unknown };
    const size = parsed.plan_size;
    if (typeof size !== "number" || !Number.isFinite(size) || size < 0) return 0;
    return Math.floor(size);
  } catch {
    return 0;
  }
}

/**
 * Raises the mark to `observedPlanSize` when that is larger. A smaller
 * observation is IGNORED — that is the whole point: the peak is what makes
 * the count condition ungameable by cancelling tickets.
 */
export async function recordG2PlanSizeHighWaterMark(
  controlRoot: string,
  observedPlanSize: number,
): Promise<void> {
  const current = await readG2PlanSizeHighWaterMark(controlRoot);
  if (observedPlanSize <= current) return;
  await writeFileDurable(
    markPath(controlRoot),
    Buffer.from(
      `${JSON.stringify({ schema_version: 1, plan_size: observedPlanSize }, null, 2)}\n`,
      "utf8",
    ),
  );
}
